/**
 * Viewport3D.ts
 * -----------------------------------------------------------------------------
 * Shared 3D viewport infrastructure used by every 3D surface in the
 * app — the main bridge viewport (`Canvas3D`) AND the floating
 * `ComponentPreview`. Wraps the boilerplate that's identical
 * between the two:
 *
 *   - WebGL renderer via `CreateConfiguredRenderer` (same antialias /
 *     log-Z / sRGB / ACES tone mapping / soft shadow map settings
 *     everywhere).
 *   - A THREE.Scene with a hemisphere + sun + fill + rim lighting
 *     rig that matches the bridge viewport's daytime defaults, so
 *     materials read identically in the preview and the model.
 *   - A perspective camera + the `camera-controls` library
 *     `CameraControls` instance (dolly-to-cursor on, same speeds,
 *     same min/max distance defaults as the main viewport). Every
 *     pan / orbit / dolly gesture behaves identically.
 *   - A per-frame loop that updates the camera controls and renders
 *     the scene, with `OnFrame(cb)` subscribers so callers can
 *     hook into the same tick.
 *   - A ResizeObserver that keeps the renderer + camera aspect in
 *     sync with the container's actual rendered size.
 *
 * Canvas3D historically did all of this inline plus a long list of
 * bridge-specific extras (BatchManager, traffic sim integration,
 * outlines, IFC export hooks, …); refactoring it to USE Viewport3D
 * is a follow-up. For now the value is that ComponentPreview can
 * instantiate Viewport3D and immediately get behaviour matching
 * the main viewport — same controls, same lighting, same render
 * pipeline — by construction, not by drift-prone manual mirroring.
 */

import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import CameraControls from 'camera-controls';

// `camera-controls` requires a one-time install with a THREE
// reference. Done here at module load so anyone importing Viewport3D
// gets a working CameraControls without further setup. Idempotent —
// safe even if some other module already installed.
CameraControls.install({ THREE });

/**
 * Wheel/pinch speed for the PERSPECTIVE camera, in camera-controls' own units
 * (`dollySpeed`). 0.5 is the dolly feel the viewport has always had; it is
 * named here only because the orthographic camera needs a different one and
 * the two have to be swapped in and out on every projection change.
 */
const PERSP_DOLLY_SPEED = 0.5;

/**
 * Wheel/pinch speed for the ORTHOGRAPHIC camera, and the value "Normal" on
 * Settings > 2D Display > Ortho Zoom Speed maps to.
 *
 * camera-controls drives BOTH actions off the single `dollySpeed` field — a
 * perspective DOLLY moves the camera (distance x 0.95^step), an orthographic
 * ZOOM scales the frustum (zoom x 0.95^step) — so one number cannot serve
 * both. At the perspective 0.5 the ortho wheel crawls: a dolly closes in on
 * the model geometrically fast because perspective magnifies as well as
 * approaches, while the frustum scale has only the one effect and needs a
 * bigger step per notch to cover the same on-screen range. 2x it is.
 */
const ORTHO_ZOOM_SPEED_DEFAULT = 1.0;

/**
 * Construct a Three.js WebGLRenderer with the canonical settings the
 * Bridge BIM viewport uses everywhere — antialias on, high-perf
 * preference, logarithmic depth buffer (so coplanar bridge surfaces
 * don't Z-fight on zoom-out), pixel ratio capped at 1.5×, sRGB
 * output, ACES tone mapping, soft shadow map. Exported so every
 * 3D surface in the app uses IDENTICAL settings — materials read
 * the same way under the same tone mapping, so previews never look
 * mismatched against the actual bridge model.
 *
 * The caller is responsible for `setSize(...)` and for appending
 * `renderer.domElement` to the right container.
 */
/** 3D toolbar ▸ Settings ▸ Line antialiasing — see Viewport3D.setAntialiasMode. */
export type AntialiasMode = 'msaa' | 'supersample';

export function CreateConfiguredRenderer(): THREE.WebGLRenderer {
  const r = new THREE.WebGLRenderer({
    antialias: true,
    powerPreference: 'high-performance',
    // Distributes depth precision across the whole near/far range so
    // distant coplanar-ish surfaces (e.g. pile-cap top + column
    // bottom) don't Z-fight when zoomed out.
    logarithmicDepthBuffer: true,
  });
  // Cap pixel ratio at 1.5 by default. On a 2× device this saves
  // ~44 % of pixel-shader work compared to the device's native ratio
  // with almost no perceived quality difference.
  r.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  r.outputColorSpace = THREE.SRGBColorSpace;
  r.toneMapping = THREE.ACESFilmicToneMapping;
  r.toneMappingExposure = 1.05;
  r.shadowMap.enabled = true;
  r.shadowMap.type = THREE.PCFSoftShadowMap;
  // The model is static, so don't re-render the (expensive 2048²) shadow map
  // every frame — render it on demand via refreshShadows() instead.
  r.shadowMap.autoUpdate = false;
  return r;
}

export interface Viewport3DOptions {
  /** Camera field of view in DEGREES. Defaults to 35 — same as the
   *  bridge viewport's perspective camera. */
  cameraFov?: number;
  /** Camera near plane in metres. Default 0.1. */
  cameraNear?: number;
  /** Camera far plane in metres. Default 5000. */
  cameraFar?: number;
  /** Background colour for the scene. Defaults to the bridge
   *  viewport's neutral dark grey. */
  backgroundColor?: number;
  /** If true (default) the viewport adds the standard
   *  hemisphere + sun + fill + rim lighting rig to the scene.
   *  Pass false when the caller wants to supply custom lighting
   *  (e.g. an outdoor day-night cycle). */
  addDefaultLights?: boolean;
  /** If true (default) the viewport assigns a RoomEnvironment PMREM
   *  to `scene.environment` so PBR materials (everything in
   *  BridgeComponents — concrete, asphalt, paint) get the same
   *  reflection IBL the main viewport uses. Without this the
   *  preview reads visibly flatter than the bridge model. */
  addDefaultEnvironment?: boolean;
  /** Initial dolly distance hint passed to CameraControls.
   *  Doesn't change the camera's position itself — call
   *  `controls.setTarget` / `setPosition` after construction to
   *  frame your scene. */
  initialMaxDistance?: number;
  /** If true (default) the viewport builds a built-in
   *  `.viewport-toolbar` overlay (Zoom In / Out / All buttons)
   *  pinned to the top-left of the container. Pass `false` only
   *  when the host page is providing its own toolbar (the main
   *  bridge viewport in index.html). New 3D surfaces should
   *  always leave this on so the user always has zoom controls.
   *  See CLAUDE.md ("Viewport zoom toolbar is mandatory"). */
  addToolbar?: boolean;
  /** Default object to frame when the toolbar's Zoom All button
   *  is clicked. Can be set later via `SetZoomAllTarget`. */
  zoomAllTarget?: THREE.Object3D;
}

/** Per-frame callback signature. dt is the delta in seconds since
 *  the previous frame, matching THREE.Clock semantics. */
export type Viewport3DFrameCallback = (dt: number) => void;

/**
 * An overlay that composites onto a finished frame.
 *
 * Takes the camera the frame was drawn with rather than reading
 * `viewport.camera`, because they are not always the same one: CaptureImage
 * frames a camera of its own for the thumbnail, and an overlay that assumed the
 * live camera would draw the live view's lines onto the thumbnail's pixels.
 */
export type Viewport3DAfterFrameCallback = (camera: THREE.Camera) => void;

const WORLD_UP = new THREE.Vector3(0, 1, 0);

/**
 * Half-extent of an axis-aligned box of the given `size` measured along an
 * arbitrary unit `axis` — the box's support function. Each world axis
 * contributes its half-size times how much of it points along `axis`, so this
 * is exact for any direction, not just the three axis-aligned ones.
 *
 * Framing used to substitute the raw box dimensions for this (height = size.y,
 * width = max(size.x, size.z)), which is only right when the camera looks
 * straight down a world axis. From the isometric home angle a 3.5 m square
 * footprint actually spans 4.8 m across the screen, and a tall object's height
 * leans into the horizontal too — so the model came out larger than the frame
 * it was given.
 */
function halfExtentAlong(size: THREE.Vector3, axis: THREE.Vector3): number {
  return 0.5 * (
    Math.abs(axis.x) * size.x +
    Math.abs(axis.y) * size.y +
    Math.abs(axis.z) * size.z
  );
}

/** The screen right/up axes for a camera offset from its target along `dir`
 *  (which need not be normalised), with no roll. */
function screenAxes(dir: THREE.Vector3): { right: THREE.Vector3; up: THREE.Vector3 } {
  const forward = dir.clone().normalize();
  const right = new THREE.Vector3().crossVectors(WORLD_UP, forward);
  // Looking straight down (or up): every horizontal direction is "right", so
  // pick one rather than normalising a zero vector into NaN.
  if (right.lengthSq() < 1e-9) right.set(1, 0, 0);
  right.normalize();
  const up = new THREE.Vector3().crossVectors(forward, right).normalize();
  return { right, up };
}

export class Viewport3D {
  public readonly container: HTMLElement;
  public readonly scene: THREE.Scene;
  /** Active camera. Starts perspective; `setProjection(true)` swaps in an
   *  orthographic camera (and back). Not readonly because the toggle replaces
   *  the instance — read it live, don't cache. */
  public camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  public readonly renderer: THREE.WebGLRenderer;
  public readonly controls: CameraControls;

  /**
   * Wheel/pinch zoom speed while the ORTHOGRAPHIC camera is active, in
   * camera-controls' `dollySpeed` units. Applied on every projection swap
   * rather than once at construction, because the perspective camera needs
   * its own (slower) value back — see setOrthoZoomSpeed.
   */
  private orthoZoomSpeed = ORTHO_ZOOM_SPEED_DEFAULT;

  /** True while an orthographic projection is active (see setProjection). */
  private isOrtho = false;

  /** True while rotation is locked (see setRotationLocked). */
  private rotationLocked = false;

  /** Lights installed by the default rig — held so callers can
   *  toggle a night mode (or whatever else) without re-discovering
   *  them via scene traversal. */
  public readonly hemi: THREE.HemisphereLight | null = null;
  public readonly sun: THREE.DirectionalLight | null = null;
  public readonly fillLight: THREE.DirectionalLight | null = null;
  public readonly rimLight: THREE.DirectionalLight | null = null;

  /** Optional Zoom-All anchor — the object the toolbar's Zoom-All
   *  button frames. Settable post-construction so callers can
   *  swap targets when the scene content changes (e.g. a fresh
   *  bridge load, a new preview component). */
  private zoomAllTarget: THREE.Object3D | null = null;
  /** The built-in `.viewport-toolbar` overlay element (Zoom In /
   *  Out / All). Null when the caller passed `addToolbar: false`
   *  — e.g. the main bridge viewport, which uses the toolbar
   *  defined in index.html. */
  public readonly toolbarEl: HTMLElement | null = null;

  /** True while the toolbar's "set orbit point" mode is armed. */
  private orbitActive = false;
  /** Pivot marker: the gray sphere at the orbit point plus the three
   *  rotation-axis lines through it. A Group, so pick filters must walk the
   *  ancestry (see isOrbitMarker) rather than compare object identity. */
  private orbitMarker: THREE.Group | null = null;

  private readonly clock = new THREE.Clock();
  private readonly onFrameCbs = new Set<Viewport3DFrameCallback>();
  /** Callbacks run AFTER `renderer.render`, for overlays that composite onto the
   *  finished frame rather than contributing scene objects to it. */
  private readonly onAfterFrameCbs = new Set<Viewport3DAfterFrameCallback>();

  /** True while the camera is being dragged or is still settling out of a drag,
   *  a wheel zoom or an animated move. Read by the after-frame overlays, which
   *  are skipped while it holds — see `renderTick`. */
  private cameraMoving = false;
  private resizeObserver: ResizeObserver | null = null;
  private rafHandle = 0;
  private disposed = false;
  /** When false the render loop is paused (e.g. while a 2D view is shown). */
  private running = true;

  /** On-demand rendering: the rAF loop always ticks `controls.update()` (cheap —
   *  it's how camera-controls detects drags/wheel/animated moves finishing), but
   *  only pays for an actual `renderer.render()` call when this is true. Starts
   *  true so the initial scene appears; every non-camera scene mutation (model
   *  load, visibility/material toggle, clip-plane drag, …) must call
   *  `invalidate()` since those don't flow through camera-controls' own change
   *  detection. */
  private needsRender = true;

  /** Main-thread time (ms) spent in the most recent render tick — controls
   *  update + OnFrame callbacks + renderer.render. Sampled by the HUD as the
   *  "CPU" frame-time metric (browsers expose no real CPU-percentage API). */
  public lastFrameMs = 0;

  constructor(container: HTMLElement, opts: Viewport3DOptions = {}) {
    this.container = container;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(opts.backgroundColor ?? 0x1c1e21);

    this.renderer = CreateConfiguredRenderer();
    this.basePixelRatio = this.renderer.getPixelRatio();
    container.appendChild(this.renderer.domElement);

    // Default near plane is intentionally tiny (1 cm) so callers
    // get matching "zoom right up to a surface" behaviour without
    // needing to override the option. Combined with the
    // logarithmicDepthBuffer enabled in CreateConfiguredRenderer
    // this stays Z-fight-free across the full 0.01–5000 m range.
    this.camera = new THREE.PerspectiveCamera(
      opts.cameraFov ?? 35,
      1,
      opts.cameraNear ?? 0.01,
      opts.cameraFar ?? 5000,
    );

    // Same CameraControls configuration as Canvas3D — dolly-to-
    // cursor on (wheel zooms toward whatever's under the pointer),
    // same dolly + truck speeds, same min/max distance defaults.
    // Any future tweak to the main viewport's feel should be
    // mirrored here so the preview and the model orbit identically.
    this.controls = new CameraControls(this.camera, this.renderer.domElement);
    this.controls.dollySpeed = PERSP_DOLLY_SPEED;
    this.controls.truckSpeed = 2.0;
    // Generous zoom clamps so wheel-scroll feels unconstrained:
    // 1 cm inner clamp lets the user dive in for surface
    // inspection (defect markers, bearing pads), and a 5 km
    // outer clamp matches the camera far plane so the longest
    // bridges still frame end-to-end. The old 1 m / 1 km
    // defaults made wheel-zoom stop short in both directions.
    this.controls.minDistance = 0.01;
    this.controls.maxDistance = opts.initialMaxDistance ?? 5000;
    this.controls.dollyToCursor = true;
    this.controls.setTarget(0, 0, 0, false);

    if (opts.addDefaultLights !== false) {
      const lights = AddDefaultViewportLights(this.scene);
      this.hemi = lights.hemi;
      this.sun = lights.sun;
      this.fillLight = lights.fill;
      this.rimLight = lights.rim;
    }

    if (opts.addDefaultEnvironment !== false) {
      AddDefaultViewportEnvironment(this.scene, this.renderer);
    }

    // Zoom-All target — settable later via SetZoomAllTarget.
    if (opts.zoomAllTarget) this.zoomAllTarget = opts.zoomAllTarget;

    // Built-in viewport toolbar — Zoom In / Out / All buttons
    // pinned to the top-left of the container. Mandatory on every
    // 3D surface so the user always has the standard controls,
    // see CLAUDE.md ("Viewport zoom toolbar is mandatory"). The
    // main bridge viewport opts out (`addToolbar: false`) because
    // its toolbar is already defined in index.html with extra
    // bridge-only buttons (isolate-selection, orbit-point, camera
    // mode). New Viewport3D consumers should leave this on.
    if (opts.addToolbar !== false) {
      // CSS `.viewport-toolbar` is `position: absolute; top: 10px;
      // left: 10px` — relies on the container being a positioned
      // ancestor. Promote `position: relative` if the caller hasn't
      // already done it so the overlay anchors correctly.
      const cs = window.getComputedStyle(container);
      if (cs.position === 'static') container.style.position = 'relative';
      this.toolbarEl = this.BuildToolbarOverlay();
      container.appendChild(this.toolbarEl);
    }

    // Track container size — the viewport can be hidden/shown via a
    // CSS attribute, so we re-measure whenever the container resizes
    // (which includes the initial show after `hidden` is cleared).
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.OnContainerResize());
      this.resizeObserver.observe(container);
    }
    this.OnContainerResize();

    // Drive a render loop that updates the camera controls and
    // re-renders every frame. Cheap enough that we don't gate on a
    // dirty flag — the main viewport doesn't either.
    this.rafHandle = window.requestAnimationFrame(() => this.renderTick());
  }

  /**
   * Set how fast the wheel (and pinch) zooms in ORTHOGRAPHIC mode, as a
   * multiple of `ORTHO_ZOOM_SPEED_DEFAULT`'s unit step — 0.5 is half as fast
   * per notch, 2 twice as fast. Driven by Settings > 2D Display > Ortho Zoom
   * Speed; the perspective dolly is deliberately left alone, since the two
   * projections need different steps to feel the same (see the constants).
   *
   * Takes effect immediately when ortho is the live projection, and is
   * remembered for the next swap into it either way. Ignores a nonsensical
   * value rather than letting it wedge the wheel: 0 would freeze zoom and a
   * negative would invert it, neither of which any caller means.
   */
  public setOrthoZoomSpeed(speed: number): void {
    if (!Number.isFinite(speed) || speed <= 0) return;
    this.orthoZoomSpeed = THREE.MathUtils.clamp(speed, 0.1, 8);
    if (this.isOrtho) this.controls.dollySpeed = this.orthoZoomSpeed;
  }

  /** Multiplicative zoom step (e.g. 0.8 = zoom in by 20%). For a perspective
   *  camera this dollies (distance × factor, clamped to min/max distance); for an
   *  orthographic camera, distance has no visual effect, so it scales the camera
   *  zoom instead (factor < 1 ⇒ zoom in ⇒ larger zoom). */
  public ZoomBy(factor: number): void {
    if (this.camera instanceof THREE.OrthographicCamera) {
      // The +/- buttons follow the wheel's speed setting, so "faster zoom"
      // means the same thing however the user zooms. Applied as an exponent
      // because the step is multiplicative: at the default speed this is
      // exactly `factor`, at 2x it is factor squared (one click covers what
      // two used to).
      const step = Math.pow(factor, this.orthoZoomSpeed / ORTHO_ZOOM_SPEED_DEFAULT);
      const next = THREE.MathUtils.clamp(
        this.camera.zoom / step,
        this.controls.minZoom,
        this.controls.maxZoom,
      );
      void this.controls.zoomTo(next, true);
      return;
    }
    const next = THREE.MathUtils.clamp(
      this.controls.distance * factor,
      this.controls.minDistance,
      this.controls.maxDistance,
    );
    void this.controls.dollyTo(next, true);
  }

  /** Zoom in by 20 % toward the current camera target. */
  public ZoomIn(): void {
    this.ZoomBy(0.8);
  }

  /** Zoom out by 25 % from the current camera target. */
  public ZoomOut(): void {
    this.ZoomBy(1.25);
  }

  /**
   * Frame the camera on the supplied object's world-space bounding
   * box, leaving `paddingFactor × radius` extra margin. Mirrors
   * `Canvas3D.ZoomAll`'s "tightly-frame-the-bbox" behaviour but
   * takes the target object explicitly because Viewport3D doesn't
   * know which subtree is the "content" — the caller (Canvas3D's
   * batch root, or ComponentPreview's contentRoot) tells us.
   *
   * `animate` defaults to true (the toolbar Zoom-All button uses
   * the animated tween). Pass false when the call is part of an
   * auto-refresh that fires on every keystroke — e.g.
   * `ComponentPreview.SetContent`, which calls this on every
   * builder run; animating each refresh would make the camera
   * lurch every time the user types a digit into a dimension
   * field.
   */
  public ZoomAll(
    target: THREE.Object3D,
    paddingFactor = 1.1,
    animate = true,
  ): void {
    this.FrameBox(new THREE.Box3().setFromObject(target), paddingFactor, animate);
  }

  /** Frame an explicit world-space bounding box (same math as ZoomAll, but the
   *  caller supplies the box — e.g. an isolated selection's true extent). */
  public FrameBox(bbox: THREE.Box3, paddingFactor = 1.1, animate = true): void {
    if (bbox.isEmpty()) return;
    // Approach from the same SW-ish three-quarter direction the preview opens
    // with (yaw -45°, pitch -30° above the horizon) so the framed view stays
    // consistent.
    const yaw = -Math.PI / 4;
    const pitch = -Math.PI / 6;
    const dir = new THREE.Vector3(
      Math.cos(pitch) * Math.sin(yaw),
      -Math.sin(pitch),
      Math.cos(pitch) * Math.cos(yaw),
    );
    this.FrameBoxFromDirection(bbox, dir, paddingFactor, animate);
  }

  /**
   * Frame `bbox` viewed from `dir` (the camera's offset direction from the box
   * centre). The one place the box → half-extents step lives: the screen right
   * and up axes are derived from `dir`, and the box is measured along all three
   * with `halfExtentAlong`, so the fit is exact from any angle rather than
   * assuming the camera looks down a world axis.
   */
  public FrameBoxFromDirection(
    bbox: THREE.Box3,
    dir: THREE.Vector3,
    paddingFactor = 1.1,
    animate = true,
  ): void {
    if (bbox.isEmpty()) return;
    const centre = bbox.getCenter(new THREE.Vector3());
    // Both projections go through FrameExtents, which frames from `dir` in
    // either. There used to be an orthographic shortcut here that handed the
    // box to camera-controls' own fitToBox — but fitToBox fits from the
    // camera's CURRENT direction and ignores `dir` entirely, so in Ortho every
    // standard view (Top, Front, Left, …) merely re-fitted the frame from
    // wherever the user was already looking and never rotated. Framing from a
    // named direction is the whole job here, so there is no shortcut to take.
    const size = bbox.getSize(new THREE.Vector3());
    const { right, up } = screenAxes(dir);
    this.FrameExtents(
      centre,
      dir,
      halfExtentAlong(size, right),
      halfExtentAlong(size, up),
      halfExtentAlong(size, dir.clone().normalize()),
      paddingFactor,
      animate,
    );
  }

  /**
   * Drop any focal offset left over from `setOrbitPoint`.
   *
   * Rotate-about-a-picked-point (the app's default rotate mode) re-pivots by
   * calling camera-controls' `setOrbitPoint`, which keeps the camera where it
   * is and absorbs the difference into `focalOffset` — a camera-local shift
   * applied AFTER position/target. `setLookAt` and `fitToBox` don't touch it,
   * so every later fit centred the target while the picture on screen stayed
   * pushed off by that offset: zoom-to-fit parked the model in a corner (or
   * off-frame entirely) once you'd clicked a surface to set an orbit point.
   * Framing means "put this box in the middle", so the offset has to go.
   */
  private ClearFocalOffset(animate: boolean): void {
    const fo = this.controls.getFocalOffset(new THREE.Vector3());
    if (fo.lengthSq() < 1e-12) return;
    void this.controls.setFocalOffset(0, 0, 0, animate);
  }

  /**
   * Frame the camera so a box with known screen-space half-extents (`halfW`
   * / `halfH`, measured perpendicular to `dir`) fits tightly, viewed from
   * `dir` (the camera's offset direction from `centre` — same convention as
   * the toolbar's named standard-view directions). Unlike `FrameBox`, which
   * always approaches from its fixed three-quarter angle, this frames from
   * whatever direction the caller supplies — used for standard views (exact
   * axis-aligned half-extents) and `ZoomAllPreserveDirection` (extents
   * measured against the camera's current actual right/up).
   */
  public FrameExtents(
    centre: THREE.Vector3,
    dir: THREE.Vector3,
    halfW: number,
    halfH: number,
    halfD = 0,
    paddingFactor = 1.1,
    animate = true,
  ): void {
    this.ClearFocalOffset(animate);
    const len = dir.length();
    // Degenerate direction (shouldn't happen for real callers) — fall back to
    // a generic forward-ish offset rather than dividing by zero.
    const unit = len > 1e-9 ? dir.clone().divideScalar(len) : new THREE.Vector3(0, 0.7, 1).normalize();
    const w = Math.max(halfW, 1e-6);
    const h = Math.max(halfH, 1e-6);
    if (this.camera instanceof THREE.PerspectiveCamera) {
      const fovV = THREE.MathUtils.degToRad(this.camera.fov);
      const fovH = 2 * Math.atan(Math.tan(fovV / 2) * this.camera.aspect);
      const distV = h / Math.tan(fovV / 2);
      const distH = w / Math.tan(fovH / 2);
      // + halfD, because the frustum widens with distance and the object's near
      // half sits closer than its centre. Sizing the camera from half-extents
      // measured at the centre plane is only right for something flat-on; give a
      // deep object (a 16 m pile group seen from the 25°-above home angle is
      // 11 m deep) that same distance and its near-top corner projects well
      // outside the frame — which is the "component doesn't fit the view" bug.
      // Pushing back by the depth half-extent guarantees the whole near face
      // fits: every corner is then at least `max(distV, distH)` away.
      const distance = halfD + Math.max(distV, distH) * paddingFactor;
      void this.controls.setLookAt(
        centre.x + unit.x * distance, centre.y + unit.y * distance, centre.z + unit.z * distance,
        centre.x, centre.y, centre.z,
        animate,
      );
    } else {
      const cam = this.camera;
      const baseHalfW = (cam.right - cam.left) / 2;
      const baseHalfH = (cam.top - cam.bottom) / 2;
      const zoom = THREE.MathUtils.clamp(
        Math.min(baseHalfW / (w * paddingFactor), baseHalfH / (h * paddingFactor)),
        this.controls.minZoom,
        this.controls.maxZoom,
      );
      // Distance has no effect on an orthographic camera's visible size, but
      // still needs to be reasonable for near/far clipping and dolly/truck feel.
      const distance = Math.max(w, h) * 3;
      void this.controls.setLookAt(
        centre.x + unit.x * distance, centre.y + unit.y * distance, centre.z + unit.z * distance,
        centre.x, centre.y, centre.z,
        false,
      );
      void this.controls.zoomTo(zoom, animate);
    }
  }

  /**
   * Zoom-to-fit while preserving the camera's CURRENT viewing direction —
   * unlike `ZoomAll`/`FrameBox`, which always reframe from the fixed "home"
   * three-quarter angle. This is what the toolbar's Zoom All button uses: if
   * the user picked a standard view (Top, Front, …) or free-orbited
   * somewhere, Zoom All should tighten the frame in place, not snap back to
   * the home angle. Falls back to `FrameBox`'s fixed direction when the
   * camera has no established direction yet (a fresh viewport, where
   * position and target still coincide).
   */
  public ZoomAllPreserveDirection(bbox: THREE.Box3, paddingFactor = 1.1, animate = true): void {
    if (bbox.isEmpty()) return;
    const centre = bbox.getCenter(new THREE.Vector3());
    const pos = this.controls.getPosition(new THREE.Vector3());
    const tgt = this.controls.getTarget(new THREE.Vector3());
    const dir = pos.sub(tgt);
    if (dir.lengthSq() < 1e-6) {
      this.FrameBox(bbox, paddingFactor, animate);
      return;
    }
    dir.normalize();
    // Safe to read back here (unlike right after we'd change direction
    // ourselves): the camera has already rendered at least one frame under
    // this exact direction, so matrixWorld's right/up columns are current.
    const right = new THREE.Vector3().setFromMatrixColumn(this.camera.matrixWorld, 0);
    const up = new THREE.Vector3().setFromMatrixColumn(this.camera.matrixWorld, 1);
    // halfD alongside halfW/halfH: how far the box reaches toward the camera,
    // which is what FrameExtents needs to keep the near corners in frame.
    let halfW = 0, halfH = 0, halfD = 0;
    const corner = new THREE.Vector3();
    for (let i = 0; i < 8; i++) {
      corner.set(
        i & 1 ? bbox.max.x : bbox.min.x,
        i & 2 ? bbox.max.y : bbox.min.y,
        i & 4 ? bbox.max.z : bbox.min.z,
      ).sub(centre);
      halfW = Math.max(halfW, Math.abs(corner.dot(right)));
      halfH = Math.max(halfH, Math.abs(corner.dot(up)));
      halfD = Math.max(halfD, Math.abs(corner.dot(dir)));
    }
    this.FrameExtents(centre, dir, halfW, halfH, halfD, paddingFactor, animate);
  }

  /** Set or replace the object the toolbar's Zoom All button
   *  frames. Pass `null` to clear (the Zoom All button becomes a
   *  no-op until a target is set again). */
  public SetZoomAllTarget(target: THREE.Object3D | null): void {
    this.zoomAllTarget = target;
  }

  /** Optional override for the Zoom All button: when set and it returns a
   *  non-empty box, Zoom All frames that box instead of the default target.
   *  Used to make Zoom All fit an isolated selection. */
  public zoomAllBox: (() => THREE.Box3 | null) | null = null;

  /** Build the floating zoom toolbar overlay. Uses the same
   *  `.viewport-toolbar` CSS class the bridge modeler's
   *  toolbar uses, so styling is shared — change one place, get
   *  consistent visuals everywhere. */
  private BuildToolbarOverlay(): HTMLElement {
    const bar = document.createElement('div');
    bar.className = 'viewport-toolbar';
    // Keep the buttons reachable even when other UI sits over the
    // scene — the CSS rule already says `pointer-events: auto`
    // but spell it out here too so the toolbar still works if
    // dropped into a host that wipes pointer-events.
    bar.style.pointerEvents = 'auto';

    const mkBtn = (
      title: string,
      svg: string,
      onClick: () => void,
    ): HTMLButtonElement => {
      const b = document.createElement('button');
      b.type = 'button';
      b.title = title;
      b.setAttribute('aria-label', title);
      b.innerHTML = svg;
      b.addEventListener('click', onClick);
      return b;
    };

    // Copies of AppIcons' zoom-in / zoom-out / zoom-all / orbit-point (Components/Shared/
    // AppIcons.cs): slate outline, faces filled from the #ricon-grad gradient the host page
    // declares once (IconDefs), highlights cut in the --layer background. Keep them in step.
    const ICO = (inner: string): string =>
      '<svg class="ico" viewBox="0 0 24 24" width="18" height="18" fill="none" ' +
      'stroke="currentColor" stroke-width="1.5" stroke-linecap="round" ' +
      'stroke-linejoin="round" aria-hidden="true">' + inner + '</svg>';
    const G = "fill='url(#ricon-grad)'";
    const HS = "style='stroke:var(--layer,#fff)'";
    const ZOOM_IN_SVG = ICO(
      `<circle cx='10.5' cy='10.5' r='6.5' ${G}/><path d='M15.5 15.5L21 21' stroke-width='2.5'/>` +
      `<path d='M10.5 7.8V13.2M7.8 10.5H13.2' ${HS} stroke-width='1.8'/>`);
    const ZOOM_OUT_SVG = ICO(
      `<circle cx='10.5' cy='10.5' r='6.5' ${G}/><path d='M15.5 15.5L21 21' stroke-width='2.5'/>` +
      `<path d='M7.8 10.5H13.2' ${HS} stroke-width='1.8'/>`);
    const ZOOM_ALL_SVG = ICO(
      `<rect x='9' y='9' width='6' height='6' rx='1' ${G}/>` +
      `<path d='M15 3H21V9M9 21H3V15M21 3L15.5 8.5M3 21L8.5 15.5'/>`);

    bar.appendChild(mkBtn('Zoom In', ZOOM_IN_SVG, () => this.ZoomIn()));
    bar.appendChild(mkBtn('Zoom Out', ZOOM_OUT_SVG, () => this.ZoomOut()));
    bar.appendChild(
      mkBtn('Zoom All', ZOOM_ALL_SVG, () => {
        const box = this.zoomAllBox?.();
        if (box && !box.isEmpty()) this.FrameBox(box);
        else if (this.zoomAllTarget) this.ZoomAll(this.zoomAllTarget);
      }),
    );

    const ORBIT_SVG = ICO(
      `<circle cx='12' cy='12' r='6'/><path d='M12 2V6M12 18V22M2 12H6M18 12H22'/>` +
      `<circle cx='12' cy='12' r='2.3' ${G}/>`);
    const orbitBtn = mkBtn('Set orbit point — click a surface (Esc to cancel)', ORBIT_SVG, () => {});
    this.bindSetOrbitPoint(orbitBtn);
    bar.appendChild(orbitBtn);

    return bar;
  }

  /** Whether the set-orbit-point mode is currently armed — hosts can read this
   *  to suppress their own click handling (e.g. selection picking). */
  public get orbitPointActive(): boolean {
    return this.orbitActive;
  }

  // Bound handlers held while the orbit-point mode is armed, so they can be
  // removed again on disarm. Null when the mode is off.
  private orbitDownHandler: ((ev: PointerEvent) => void) | null = null;
  private orbitKeyHandler: ((ev: KeyboardEvent) => void) | null = null;
  private readonly orbitRaycaster = new THREE.Raycaster();
  private readonly orbitNdc = new THREE.Vector2();

  /** Arm / disarm "set orbit point" mode: while armed the cursor is a
   *  crosshair and a marker shows the pivot; clicking a surface re-pivots the
   *  camera there (so the next drag orbits around it). This is the toolbar's
   *  "rotate about a picked point" mode (vs the default global rotate). Generic
   *  — raycasts whatever is in the scene, with no knowledge of any app's model. */
  public setOrbitPointMode(on: boolean): void {
    if (on === this.orbitActive) return;
    if (on) {
      this.orbitActive = true;
      this.container.style.cursor = 'crosshair';
      const marker = this.getOrCreateOrbitMarker();
      this.updateOrbitMarker();
      marker.visible = true;
      this.orbitDownHandler = (ev) => this.onOrbitDown(ev);
      this.orbitKeyHandler = (ev) => {
        if (ev.key === 'Escape') this.setOrbitPointMode(false);
      };
      // Capture phase: fire before camera-controls' own pointerdown handler.
      this.container.addEventListener('pointerdown', this.orbitDownHandler, true);
      window.addEventListener('keydown', this.orbitKeyHandler);
    } else {
      this.orbitActive = false;
      if (this.orbitDownHandler)
        this.container.removeEventListener('pointerdown', this.orbitDownHandler, true);
      if (this.orbitKeyHandler) window.removeEventListener('keydown', this.orbitKeyHandler);
      this.orbitDownHandler = null;
      this.orbitKeyHandler = null;
      this.container.style.cursor = '';
      if (this.orbitMarker) this.orbitMarker.visible = false;
    }
    this.needsRender = true;
  }

  private onOrbitDown(ev: PointerEvent): void {
    if (ev.button !== 0) return;
    const rect = this.container.getBoundingClientRect();
    this.orbitNdc.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
    this.orbitNdc.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
    this.orbitRaycaster.setFromCamera(this.orbitNdc, this.camera);
    const hits = this.orbitRaycaster.intersectObjects(this.scene.children, true);
    // Never pivot on the reference grid: its translucent fill quads and text
    // labels are THREE.Mesh, so the "solid surface" filter alone would let a
    // back-wall/floor panel behind the model win the click (the symptom: the
    // camera orbits a point at the back of the box). Walk up each hit's
    // ancestry and drop anything under the AxesGrid, plus the orbit marker.
    // Hidden geometry too: three.js raycasts invisible objects. The Explorer's
    // FE Mesh view hides the bridge and keeps only one component's see-through
    // solid — without this the hidden deck above a column took the click.
    // `userData.noOrbitPick`: tools drawn over the model (the custom-mesh gizmo) are not
    // places to orbit about.
    const pickable = hits.filter((h) => {
      if (this.isOrbitMarker(h.object)) return false;
      for (let o: THREE.Object3D | null = h.object; o; o = o.parent) {
        if (o.name === 'AxesGrid' || !o.visible || o.userData.noOrbitPick) return false;
      }
      return true;
    });
    // Prefer a solid surface (closest mesh); three.js fat-picks line geometry
    // within a threshold, so a mesh under the cursor should beat nearby edges.
    // But in line/wireframe display mode there is no surface — fall back to the
    // closest remaining hit (model edges) so the pivot still snaps to geometry.
    const hit =
      pickable.find((h) => h.object instanceof THREE.Mesh) ?? pickable[0];
    if (!hit) return;
    const p = hit.point;
    // Re-pivot to the picked point; the same pointer event keeps bubbling
    // into camera-controls, so a drag right after orbits around it.
    this.controls.setOrbitPoint(p.x, p.y, p.z);
    this.updateOrbitMarker();
  }

  /** Wire the built-in toolbar's orbit button to toggle the mode above. */
  private bindSetOrbitPoint(btn: HTMLButtonElement): void {
    btn.addEventListener('click', () => {
      this.setOrbitPointMode(!this.orbitActive);
      btn.classList.toggle('is-active', this.orbitActive);
    });
  }

  /**
   * Switch the active camera between perspective and orthographic, preserving
   * the on-screen framing. The orthographic frustum is sized so the model
   * appears the same size at the current orbit distance, giving a seamless
   * toggle. camera-controls supports both projection types on the same
   * controls instance (its `camera` is reassignable). Wheel zoom automatically
   * becomes a zoom-factor change in orthographic mode.
   */
  public setProjection(ortho: boolean): void {
    if (ortho === this.isOrtho || this.disposed) return;
    const target = this.controls.getTarget(new THREE.Vector3());
    const position = this.controls.getPosition(new THREE.Vector3());
    const rect = this.container.getBoundingClientRect();
    const aspect = Math.max(64, rect.width) / Math.max(64, rect.height);
    const old = this.camera;

    let next: THREE.PerspectiveCamera | THREE.OrthographicCamera;
    if (ortho) {
      const dist = position.distanceTo(target);
      const fov = old instanceof THREE.PerspectiveCamera ? old.fov : 35;
      const halfH = Math.tan(THREE.MathUtils.degToRad(fov) / 2) * dist;
      const halfW = halfH * aspect;
      next = new THREE.OrthographicCamera(-halfW, halfW, halfH, -halfH, old.near, old.far);
    } else {
      next = new THREE.PerspectiveCamera(35, aspect, old.near, old.far);
    }
    next.position.copy(position);
    next.up.copy(old.up);
    next.lookAt(target);
    next.updateProjectionMatrix();

    this.camera = next;
    this.isOrtho = ortho;
    // Rebind controls to the new camera and resync its internal spherical state.
    this.controls.camera = next;
    this.controls.updateCameraUp();
    // The wheel/pinch zoom action is camera-type specific and is NOT re-derived
    // when the camera is swapped: perspective dollies, orthographic zooms. Without
    // this, wheel/pinch (and any dolly-based zoom) silently do nothing in ortho.
    // `dollySpeed` serves both actions, so it has to be swapped alongside them
    // or ortho inherits the perspective dolly's (much slower) step.
    this.controls.dollySpeed = ortho ? this.orthoZoomSpeed : PERSP_DOLLY_SPEED;
    if (ortho) {
      this.controls.mouseButtons.wheel = CameraControls.ACTION.ZOOM;
      this.controls.touches.two = CameraControls.ACTION.TOUCH_ZOOM_TRUCK;
      this.controls.touches.three = CameraControls.ACTION.TOUCH_ZOOM_TRUCK;
    } else {
      this.controls.mouseButtons.wheel = CameraControls.ACTION.DOLLY;
      this.controls.touches.two = CameraControls.ACTION.TOUCH_DOLLY_TRUCK;
      this.controls.touches.three = CameraControls.ACTION.TOUCH_DOLLY_TRUCK;
    }
    void this.controls.setLookAt(
      position.x, position.y, position.z,
      target.x, target.y, target.z,
      false,
    );
  }

  /** True while an orthographic projection is active. */
  public get orthographic(): boolean {
    return this.isOrtho;
  }

  /**
   * Lock or unlock camera rotation, leaving pan and zoom untouched. Used for
   * the axis-aligned orthographic views (Top/Bottom/Front/Back/Left/Right):
   * rotating away from them defeats the point of an axis-aligned view, so the
   * host disables rotation for all of them except Isometric. Only the
   * rotate-drag bindings (left mouse button, single-touch drag) are disabled —
   * pan (right-drag / two-finger) and dolly/zoom (wheel / pinch) still work.
   */
  public setRotationLocked(locked: boolean): void {
    if (locked === this.rotationLocked) return;
    this.rotationLocked = locked;
    this.controls.mouseButtons.left = locked
      ? CameraControls.ACTION.NONE
      : CameraControls.ACTION.ROTATE;
    this.controls.touches.one = locked
      ? CameraControls.ACTION.NONE
      : CameraControls.ACTION.TOUCH_ROTATE;
  }

  /** True while rotation is locked (see setRotationLocked). */
  public get rotationIsLocked(): boolean {
    return this.rotationLocked;
  }

  /**
   * One-shot "zoom window": the user drags a rectangle over the canvas and the
   * camera fits that screen region. An HTML overlay shows the marquee; camera
   * controls are suspended during the drag. On release the four rectangle
   * corners are unprojected onto the plane through the picked focus point and
   * `controls.fitToBox` frames the resulting world box — works for both
   * perspective and orthographic cameras. Esc cancels.
   */
  public zoomWindow(): void {
    if (this.disposed) return;
    const el = this.container;
    const overlay = document.createElement('div');
    overlay.style.cssText =
      'position:absolute;left:0;top:0;width:0;height:0;z-index:6;pointer-events:none;' +
      'border:1px solid var(--accent,#0067c0);background:rgba(0,103,192,0.12);';
    const prevCursor = el.style.cursor;
    const prevEnabled = this.controls.enabled;
    // The overlay is absolutely positioned — make sure the container is a
    // positioned ancestor (it usually is, but Viewport3D is shared code).
    const prevPosition = el.style.position;
    if (window.getComputedStyle(el).position === 'static') el.style.position = 'relative';
    el.style.cursor = 'crosshair';
    this.controls.enabled = false;

    let startX = 0, startY = 0, dragging = false;

    const cleanup = (): void => {
      el.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', onUp, true);
      window.removeEventListener('keydown', onKey, true);
      if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
      el.style.cursor = prevCursor;
      el.style.position = prevPosition;
      this.controls.enabled = prevEnabled;
    };

    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') cleanup();
    };
    const onDown = (ev: PointerEvent): void => {
      if (ev.button !== 0) { cleanup(); return; }
      ev.preventDefault();
      ev.stopPropagation();
      dragging = true;
      const r = el.getBoundingClientRect();
      startX = ev.clientX - r.left;
      startY = ev.clientY - r.top;
      overlay.style.left = `${startX}px`;
      overlay.style.top = `${startY}px`;
      overlay.style.width = '0px';
      overlay.style.height = '0px';
      el.appendChild(overlay);
    };
    const onMove = (ev: PointerEvent): void => {
      if (!dragging) return;
      const r = el.getBoundingClientRect();
      const x = ev.clientX - r.left, y = ev.clientY - r.top;
      overlay.style.left = `${Math.min(startX, x)}px`;
      overlay.style.top = `${Math.min(startY, y)}px`;
      overlay.style.width = `${Math.abs(x - startX)}px`;
      overlay.style.height = `${Math.abs(y - startY)}px`;
    };
    const onUp = (ev: PointerEvent): void => {
      if (!dragging) { cleanup(); return; }
      const r = el.getBoundingClientRect();
      const x = ev.clientX - r.left, y = ev.clientY - r.top;
      const x0 = Math.min(startX, x), y0 = Math.min(startY, y);
      const w = Math.abs(x - startX), h = Math.abs(y - startY);
      cleanup();
      if (w < 6 || h < 6) return; // too small to be a deliberate window
      this.fitToScreenRect(r.width, r.height, x0, y0, w, h);
    };

    el.addEventListener('pointerdown', onDown, true);
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp, true);
    window.addEventListener('keydown', onKey, true);
  }

  /** Fit the camera to a screen-space rectangle (CSS px within the canvas). */
  private fitToScreenRect(
    cw: number, ch: number,
    rx: number, ry: number, rw: number, rh: number,
  ): void {
    const ray = new THREE.Raycaster();
    // Focus depth: raycast the rectangle centre; fall back to the orbit target.
    const cNdc = new THREE.Vector2(
      ((rx + rw / 2) / cw) * 2 - 1,
      -((ry + rh / 2) / ch) * 2 + 1,
    );
    ray.setFromCamera(cNdc, this.camera);
    const hits = ray
      .intersectObjects(this.scene.children, true)
      .filter((hh) => {
        if (this.isOrbitMarker(hh.object)) return false;
        // Hidden geometry is raycast too — see onOrbitDown.
        for (let o: THREE.Object3D | null = hh.object; o; o = o.parent) if (!o.visible) return false;
        return true;
      });
    const focus = hits.length
      ? hits[0].point.clone()
      : this.controls.getTarget(new THREE.Vector3());

    // Plane through the focus point, facing the camera.
    const fwd = new THREE.Vector3();
    this.camera.getWorldDirection(fwd);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(fwd, focus);

    const box = new THREE.Box3();
    const p = new THREE.Vector3();
    let ok = false;
    const corners: Array<[number, number]> = [
      [rx, ry], [rx + rw, ry], [rx, ry + rh], [rx + rw, ry + rh],
    ];
    for (const [sx, sy] of corners) {
      const nx = (sx / cw) * 2 - 1;
      const ny = -(sy / ch) * 2 + 1;
      ray.setFromCamera(new THREE.Vector2(nx, ny), this.camera);
      if (ray.ray.intersectPlane(plane, p)) {
        box.expandByPoint(p.clone());
        ok = true;
      }
    }
    if (!ok || box.isEmpty()) return;
    this.ClearFocalOffset(true);
    void this.controls.fitToBox(box, true);
  }

  /** Pivot sphere radius, as a fraction of the viewport half-height measured
   *  at the pivot's depth. Small on purpose: the marker should read as a
   *  precise point, not cover the surface it was dropped on. */
  private static readonly ORBIT_MARKER_R = 0.011;
  /** Half-length of each rotation-axis line, in multiples of the sphere
   *  radius. */
  private static readonly ORBIT_AXIS_LEN = 6;

  private getOrCreateOrbitMarker(): THREE.Group {
    if (!this.orbitMarker) {
      const group = new THREE.Group();
      group.name = 'OrbitMarker';
      // Built at unit radius — the real size is set per-frame by
      // updateOrbitMarkerScale, which scales the whole group.
      group.renderOrder = 999;

      const geom = new THREE.SphereGeometry(1, 20, 14);
      const mat = new THREE.MeshBasicMaterial({
        color: 0x8c8c8c,
        depthTest: false,
        transparent: true,
        opacity: 0.85,
      });
      const ball = new THREE.Mesh(geom, mat);
      ball.renderOrder = 999;
      group.add(ball);

      // The three rotation axes through the pivot (X red, Y green, Z blue),
      // each drawn both ways so the pivot reads as a 3D crosshair from any
      // viewing angle and the drag axis is obvious before the drag starts.
      const L = Viewport3D.ORBIT_AXIS_LEN;
      const axes: Array<[THREE.Vector3, number]> = [
        [new THREE.Vector3(1, 0, 0), 0xd8503c],
        [new THREE.Vector3(0, 1, 0), 0x4f9e4f],
        [new THREE.Vector3(0, 0, 1), 0x3f72c4],
      ];
      for (const [dir, color] of axes) {
        const g = new THREE.BufferGeometry().setFromPoints([
          dir.clone().multiplyScalar(-L),
          dir.clone().multiplyScalar(L),
        ]);
        const m = new THREE.LineBasicMaterial({
          color,
          depthTest: false,
          transparent: true,
          opacity: 0.9,
        });
        const line = new THREE.Line(g, m);
        line.renderOrder = 999;
        group.add(line);
      }

      this.orbitMarker = group;
      this.scene.add(group);
    }
    return this.orbitMarker;
  }

  /** True when `obj` is part of the pivot marker (sphere or an axis line).
   *  The marker is a Group, so pick filters have to walk the ancestry rather
   *  than compare against a single mesh. */
  private isOrbitMarker(obj: THREE.Object3D): boolean {
    const mk = this.orbitMarker;
    if (!mk) return false;
    for (let o: THREE.Object3D | null = obj; o; o = o.parent) {
      if (o === mk) return true;
    }
    return false;
  }

  private updateOrbitMarker(): void {
    if (!this.orbitMarker) return;
    const t = new THREE.Vector3();
    this.controls.getTarget(t);
    this.orbitMarker.position.copy(t);
    this.updateOrbitMarkerScale();
  }

  /** Keep the orbit marker at a roughly constant on-screen size by scaling it
   *  with the camera distance (so it doesn't balloon when zoomed in close). */
  private updateOrbitMarkerScale(): void {
    const mk = this.orbitMarker;
    if (!mk || !mk.visible) return;
    const f = Viewport3D.ORBIT_MARKER_R;
    let r: number;
    if (this.camera instanceof THREE.PerspectiveCamera) {
      const d = this.camera.position.distanceTo(mk.position);
      const halfFov = THREE.MathUtils.degToRad(this.camera.fov) / 2;
      r = d * Math.tan(halfFov) * f;
    } else {
      // Orthographic: on-screen size is governed by the frustum height / zoom,
      // not camera distance.
      const cam = this.camera;
      r = ((cam.top - cam.bottom) / cam.zoom) * f * 0.5;
    }
    mk.scale.setScalar(Math.max(1e-4, r));
  }

  /** Register a per-frame callback. Returns the unsubscriber. */
  public OnFrame(cb: Viewport3DFrameCallback): () => void {
    this.onFrameCbs.add(cb);
    return () => {
      this.onFrameCbs.delete(cb);
    };
  }

  /**
   * Register a callback that runs immediately AFTER `renderer.render`, on the
   * frames that actually draw. For a pass that composites over the finished
   * image — a screen-space overlay — which cannot use OnFrame, because at that
   * point there is no finished image to draw on.
   *
   * A callback here inherits the renderer mid-frame and must put back anything
   * it changes: the render target, autoClear, the clear colour and the camera's
   * layer mask are all shared with the shadow refresh and the thumbnail capture.
   *
   * SKIPPED WHILE THE CAMERA IS MOVING. These passes cost a render each, and a
   * drag is exactly when the frame budget is spent and when a static-looking
   * detail is least missed. `renderTick` forces one more frame the moment motion
   * stops, so the overlay lands on the settled view rather than waiting for
   * something else to invalidate. Thumbnail capture runs them unconditionally —
   * a saved still is not interactive and has no budget to protect.
   */
  public OnAfterFrame(cb: Viewport3DAfterFrameCallback): () => void {
    this.onAfterFrameCbs.add(cb);
    return () => {
      this.onAfterFrameCbs.delete(cb);
    };
  }

  /** True while the camera is moving. After-frame overlays are skipped for the
   *  duration; see `renderTick` for how the settled frame gets drawn. */
  public get isCameraMoving(): boolean {
    return this.cameraMoving;
  }

  /** 3D toolbar ▸ Settings ▸ Line antialiasing. `msaa` is the renderer's own 4x MSAA
   *  (CreateConfiguredRenderer); `supersample` adds a frame drawn at twice the pixel
   *  ratio, which the browser box-filters down to the canvas — about 4x the pixel cost.
   *
   *  Two more were tried and dropped as having no visible effect in the WebView:
   *  three's fat lines with a shader coverage falloff, and an FXAA pass. */
  private aaMode: AntialiasMode = 'msaa';
  /** The pixel ratio CreateConfiguredRenderer chose — what supersampling doubles. */
  private readonly basePixelRatio: number;

  public get antialiasMode(): AntialiasMode { return this.aaMode; }

  public setAntialiasMode(mode: AntialiasMode): void {
    if (mode === this.aaMode) return;
    this.aaMode = mode;
    // Twice the base ratio, but no more than 3: a 4K canvas at 2x1.5 is already a
    // 7680-wide buffer, and past that the GPU pays for pixels nobody can see.
    const ratio = mode === 'supersample' ? Math.min(3, this.basePixelRatio * 2) : this.basePixelRatio;
    if (ratio !== this.renderer.getPixelRatio()) {
      this.renderer.setPixelRatio(ratio);
      this.OnContainerResize();
    }
    this.invalidate();
  }

  /** The one place the main frame is drawn. */
  private renderMain(camera: THREE.Camera): void {
    this.renderer.render(this.scene, camera);
  }

  /** Run the after-frame overlays against the camera a frame was just drawn
   *  with. Each is responsible for restoring any renderer state it changes. */
  private runAfterFrame(camera: THREE.Camera): void {
    for (const cb of this.onAfterFrameCbs) cb(camera);
  }

  /** Tear down WebGL + listeners. After Dispose, the viewport's
   *  resources are released; constructing a new Viewport3D is the
   *  only way to get a working viewport back. */
  public Dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.rafHandle) {
      window.cancelAnimationFrame(this.rafHandle);
      this.rafHandle = 0;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.controls.dispose();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
    if (this.renderer.domElement.parentElement === this.container) {
      this.container.removeChild(this.renderer.domElement);
    }
    if (this.toolbarEl && this.toolbarEl.parentElement === this.container) {
      this.container.removeChild(this.toolbarEl);
    }
  }

  /** Request a render on the next tick. Camera moves/animations don't need
   *  this — `controls.update()` reports those itself — but any other scene
   *  mutation does: geometry loads, visibility/material/opacity changes,
   *  clip-plane edits, shadow toggles, resizes, theme changes, and the like.
   *  Cheap and idempotent to call as often as needed. */
  public invalidate(): void {
    this.needsRender = true;
  }

  /** The render loop body: always ticks camera-controls (cheap — this is how
   *  it detects a drag/wheel/animated move is still in progress or has just
   *  finished) but only pays for an actual `renderer.render()` call when
   *  something has actually changed, i.e. on-demand rendering rather than a
   *  fixed 60fps redraw. Re-schedules itself while `running`. */
  private renderTick(): void {
    if (this.disposed || !this.running) return;
    this.rafHandle = window.requestAnimationFrame(() => this.renderTick());
    const dt = this.clock.getDelta();
    const moving = this.controls.update(dt);
    if (moving) {
      this.needsRender = true;
    } else if (this.cameraMoving) {
      // MOTION JUST STOPPED, so force one more frame. Without it the last frame
      // drawn is the one from mid-drag — the frame the after-frame overlays
      // skipped themselves on — and since nothing else is going to invalidate a
      // settled viewport, their lines would stay missing until the user touched
      // something. This is the frame they come back on.
      this.needsRender = true;
    }
    // Set BEFORE the render, so an overlay reading `isCameraMoving` sees the
    // state of the frame being drawn rather than of the frame before it.
    this.cameraMoving = moving;
    if (!this.needsRender) return;
    const t0 = performance.now();
    // Position AND scale: the pivot marker rides `controls.target`, which moves
    // on a pan/truck and on every zoom-to-fit, so syncing it only when the mode
    // is armed or a new point is picked left it stranded at a stale spot,
    // pointing at something that was no longer the pivot.
    this.updateOrbitMarker();
    for (const cb of this.onFrameCbs) cb(dt);
    this.renderMain(this.camera);
    // Overlays only on a settled view — see OnAfterFrame.
    if (!moving) this.runAfterFrame(this.camera);
    this.lastFrameMs = performance.now() - t0;
    this.needsRender = false;
  }

  /** Pause or resume the render loop. Pausing (e.g. while a 2D view is shown)
   *  stops all per-frame GPU/CPU work; resuming restarts it cleanly. */
  public setRenderingEnabled(on: boolean): void {
    if (on === this.running) return;
    this.running = on;
    if (on) {
      this.clock.getDelta(); // discard the long pause delta
      this.needsRender = true; // the container/content may have changed while paused
      this.rafHandle = window.requestAnimationFrame(() => this.renderTick());
    } else if (this.rafHandle) {
      window.cancelAnimationFrame(this.rafHandle);
      this.rafHandle = 0;
    }
  }

  /**
   * Render an off-view still of the scene and hand it back as a PNG data URL —
   * the Recent Projects thumbnail (see Components/Services/ProjectThumbnails.cs).
   *
   * Deliberately NOT a screenshot of what the user is looking at: the caller
   * passes the whole model's bounding box and a viewing direction, and this
   * frames a CAMERA OF ITS OWN on them (same math as `FrameExtents`, minus
   * camera-controls). So the live camera, its target, its focal offset and the
   * user's projection mode are all left exactly as they were, and the thumbnail
   * shows the entire bridge however far the user happened to be zoomed in.
   *
   * Renders through the SAME renderer as the live view rather than an offscreen
   * render target, because three.js disables tone mapping when the destination
   * is a render target — a target capture would come out visibly unlike the
   * bridge on screen (see `CreateConfiguredRenderer`). The drawing buffer is
   * resized for the shot and the live view is repainted synchronously before
   * this returns, so the compositor never gets to show the intermediate frame.
   *
   * @param box     World-space box to frame (the model's full bounds).
   * @param dir     Camera offset direction from the box centre.
   * @param width   Thumbnail width in pixels.
   * @param height  Thumbnail height in pixels.
   * @param paddingFactor Margin around the framed box, 1 = tight.
   * @param type    Encoding, e.g. 'image/png' or 'image/jpeg'.
   * @param quality Lossy-format quality, 0..1. Ignored for PNG.
   * @returns A `data:<type>;base64,…` URL, or '' when there is nothing to
   *          render (disposed viewport / empty box / a blocked canvas read).
   */
  public CaptureImage(
    box: THREE.Box3,
    dir: THREE.Vector3,
    width: number,
    height: number,
    paddingFactor = 1.12,
    type = 'image/png',
    quality = 0.85,
  ): string {
    if (this.disposed || box.isEmpty()) return '';

    const w = Math.max(16, Math.round(width));
    const h = Math.max(16, Math.round(height));

    // Match the live camera's field of view so the thumbnail's perspective
    // reads the same as the model does on screen.
    const fov = this.camera instanceof THREE.PerspectiveCamera ? this.camera.fov : 35;
    const cam = new THREE.PerspectiveCamera(fov, w / h, 0.01, 5000);

    const centre = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const unit = dir.lengthSq() > 1e-9
      ? dir.clone().normalize()
      : new THREE.Vector3(0, 0.7, 1).normalize();

    // Half-extents measured along the picture's own right/up axes, plus the
    // depth term — same reasoning as FrameExtents: a deep object viewed from a
    // diagonal needs pushing back by its depth or its near corner projects
    // outside the frame.
    const { right, up } = screenAxes(unit);
    const halfW = Math.max(halfExtentAlong(size, right), 1e-6);
    const halfH = Math.max(halfExtentAlong(size, up), 1e-6);
    const halfD = halfExtentAlong(size, unit);
    const fovV = THREE.MathUtils.degToRad(fov);
    const fovH = 2 * Math.atan(Math.tan(fovV / 2) * cam.aspect);
    const distance = halfD + Math.max(halfH / Math.tan(fovV / 2), halfW / Math.tan(fovH / 2)) * paddingFactor;

    cam.up.copy(WORLD_UP);
    cam.position.set(
      centre.x + unit.x * distance,
      centre.y + unit.y * distance,
      centre.z + unit.z * distance,
    );
    cam.lookAt(centre);
    // Clip planes sized to this shot: a 5 km far plane is right for the live
    // camera but would leave a long bridge's far end outside a tight frame.
    cam.near = Math.max(0.01, distance * 1e-4);
    cam.far = distance + halfD * 4 + Math.max(size.x, size.y, size.z);
    cam.updateProjectionMatrix();

    // The rotate-about-a-point pivot marker is interaction furniture, not part
    // of the bridge — it has no business in a saved thumbnail.
    const marker = this.orbitMarker;
    const markerWasVisible = marker?.visible ?? false;

    const prevSize = this.renderer.getSize(new THREE.Vector2());
    const prevRatio = this.renderer.getPixelRatio();

    try {
      if (marker) marker.visible = false;
      // Pixel ratio 1: `width`/`height` are the pixels the caller asked for,
      // not a HiDPI multiple of them.
      this.renderer.setPixelRatio(1);
      this.renderer.setSize(w, h, false);
      this.renderer.shadowMap.needsUpdate = true;
      this.renderMain(cam);
      // The screen-space overlays, against the THUMBNAIL's camera and before the
      // read-back — they draw onto the finished frame, so after this the buffer
      // holds the picture and before it the picture is unfinished. Run
      // unconditionally: a still is not interactive, so the drag-skip that
      // applies in `renderTick` has nothing to protect here.
      this.runAfterFrame(cam);
      // Read back BEFORE yielding: the renderer is created without
      // preserveDrawingBuffer, so the buffer is only guaranteed to hold the
      // frame until the next composite.
      return this.renderer.domElement.toDataURL(type, quality);
    } catch (e) {
      console.warn('[Viewport3D] thumbnail capture failed', e);
      return '';
    } finally {
      if (marker) marker.visible = markerWasVisible;
      this.renderer.setPixelRatio(prevRatio);
      // getSize() reports the CSS size, not the drawing-buffer size, so it
      // restores as-is — dividing by the pixel ratio here would leave the live
      // view rendering into a buffer 1/dpr the size the canvas is stretched to.
      this.renderer.setSize(prevSize.x, prevSize.y, false);
      this.renderer.shadowMap.needsUpdate = true;
      // Repaint the live view now, in this same synchronous block, so the
      // thumbnail-sized frame is never what the compositor picks up. Its
      // overlays go back on too — `needsRender` is cleared on the next line, so
      // this repaint is the last frame until something invalidates, and leaving
      // the overlays off it would drop the lines off the live view for as long
      // as the user did not touch anything.
      this.renderMain(this.camera);
      this.runAfterFrame(this.camera);
      this.needsRender = false;
    }
  }

  /** Recompute renderer size + camera aspect from the container's
   *  current bounding rect. Called automatically on container
   *  resize; expose it publicly so callers that manually adjust
   *  the container size (e.g. a collapse/expand toggle) can force
   *  a re-measure on demand. */
  public OnContainerResize(): void {
    const rect = this.container.getBoundingClientRect();
    const w = Math.max(64, rect.width);
    const h = Math.max(64, rect.height);
    this.renderer.setSize(w, h, false);
    if (this.camera instanceof THREE.PerspectiveCamera) {
      this.camera.aspect = w / h;
    } else {
      // Orthographic: keep the frustum height, re-derive left/right from aspect.
      const cam = this.camera;
      const halfH = (cam.top - cam.bottom) / 2;
      const halfW = halfH * (w / h);
      cam.left = -halfW;
      cam.right = halfW;
    }
    this.camera.updateProjectionMatrix();
    this.needsRender = true;
  }

  /** Enable or disable real-time shadows. Toggles the shadow map and the
   *  sun's shadow casting, and flags materials for recompile so the shadow
   *  code is added/removed. Renders on-demand, so this also has to ask for
   *  that next render explicitly. */
  public setShadowsEnabled(on: boolean): void {
    this.renderer.shadowMap.enabled = on;
    this.renderer.shadowMap.needsUpdate = true;
    if (this.sun) this.sun.castShadow = on;
    this.scene.traverse((o) => {
      const mat = (o as THREE.Mesh).material;
      if (Array.isArray(mat)) mat.forEach((m) => (m.needsUpdate = true));
      else if (mat) (mat as THREE.Material).needsUpdate = true;
    });
    this.needsRender = true;
  }

  /** Re-render the on-demand shadow map once on the next frame. Call after the
   *  scene's shadow casters/visibility change (model load, story filter, layer
   *  toggle) since shadowMap.autoUpdate is off. */
  public refreshShadows(): void {
    this.renderer.shadowMap.needsUpdate = true;
    this.needsRender = true;
  }
}

/**
 * Apply the canonical "daytime bridge" environment to a scene —
 * sky gradient background, light exponential fog, PBR
 * RoomEnvironment for image-based reflections, and an invisible
 * shadow-catcher plane at y = −0.01 so directional-light shadows
 * have somewhere to land. Mirrors `Canvas3D.SetupEnvironment` so
 * the preview reads identically to the main viewport — same sky
 * tones, same fog density, same PBR IBL, same shadow grounding.
 *
 * All four pieces matter:
 *
 *   - Sky background: warms the upper portion of the canvas, gives
 *     PBR reflections a coloured horizon, anchors the model
 *     against a horizon line instead of solid black.
 *   - Fog: a very light FogExp2 at 0.0006 — 98 % clear at 200 m,
 *     ~70 % at 1 km — sells depth without hazing close-up geometry.
 *   - RoomEnvironment PMREM: drives reflections + indirect light on
 *     PBR materials (concrete, asphalt, paint). Without it
 *     metalness ≠ 0 surfaces render flat.
 *   - Shadow catcher: invisible plane (ShadowMaterial, opacity 0.4)
 *     that grounds the model — the bent's shadow falls onto it
 *     instead of being lost into the void. `depthWrite: false`
 *     keeps it out of the depth buffer so it can't Z-fight with
 *     pile caps or footings that pass through y = 0.
 */
export function AddDefaultViewportEnvironment(
  scene: THREE.Scene,
  renderer: THREE.WebGLRenderer,
): void {
  // --- Sky gradient background ---
  const skyCanvas = document.createElement('canvas');
  skyCanvas.width = 2;
  skyCanvas.height = 512;
  const ctx = skyCanvas.getContext('2d');
  if (ctx) {
    const grad = ctx.createLinearGradient(0, 0, 0, 512);
    grad.addColorStop(0.0, '#5b6a7d');
    grad.addColorStop(0.45, '#343b46');
    grad.addColorStop(1.0, '#1a1d22');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 2, 512);
    const skyTex = new THREE.CanvasTexture(skyCanvas);
    skyTex.colorSpace = THREE.SRGBColorSpace;
    scene.background = skyTex;
  }

  // --- Fog ---
  scene.fog = new THREE.FogExp2(0x2e3540, 0.0006);

  // --- PBR environment map (RoomEnvironment) ---
  const pmrem = new THREE.PMREMGenerator(renderer);
  const envScene = new RoomEnvironment();
  scene.environment = pmrem.fromScene(envScene, 0.04).texture;
  pmrem.dispose();

  // --- Invisible shadow catcher at ground level (disabled) ---
  // The ground shadow-catcher plane is intentionally not added: the
  // projected "fake" ground shadow has been turned off.
}

/**
 * Add the canonical "daytime bridge" lighting rig to a scene —
 * hemisphere fill + a warm sun key light + a cool side fill + a
 * warm rim. Numbers mirror Canvas3D's `SetupLighting` so previews
 * inherit the exact same look as the main viewport. Returns the
 * lights so the caller can hold references for night-mode swaps
 * etc.
 */
export function AddDefaultViewportLights(scene: THREE.Scene): {
  hemi: THREE.HemisphereLight;
  sun: THREE.DirectionalLight;
  fill: THREE.DirectionalLight;
  rim: THREE.DirectionalLight;
} {
  // Ground colour brightened well past a literal "dirt" tone — the
  // hemisphere light's ground component is the ONLY thing lighting
  // downward-facing normals (girder undersides, soffits), since the
  // sun/fill/rim below are all directional lights positioned above
  // the deck. Too dark here and the bridge's underside renders
  // near-black despite the rest of the model reading fine.
  const hemi = new THREE.HemisphereLight(0xa8c4e8, 0x746757, 0.85);
  hemi.position.set(0, 50, 0);
  scene.add(hemi);

  const sun = new THREE.DirectionalLight(0xfff4dd, 3.0);
  sun.position.set(45, 75, 30);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = 260;
  const ext = 75;
  sun.shadow.camera.left = -ext;
  sun.shadow.camera.right = ext;
  sun.shadow.camera.top = ext;
  sun.shadow.camera.bottom = -ext;
  sun.shadow.bias = -0.0001;
  sun.shadow.normalBias = 0.04;
  sun.shadow.radius = 3;
  scene.add(sun);
  scene.add(sun.target);

  const fill = new THREE.DirectionalLight(0x8da9c8, 0.55);
  fill.position.set(-35, 30, -25);
  scene.add(fill);

  const rim = new THREE.DirectionalLight(0xffb380, 0.4);
  rim.position.set(-10, 18, -50);
  scene.add(rim);

  // Bounce light: a dim directional light aimed upward from below the deck,
  // approximating light bouncing off the ground/road under a bridge so the
  // underside of girders/soffits/pile caps isn't lit by the hemisphere ground
  // term alone. No shadows (would double-darken the same surfaces it's meant
  // to lift) and low intensity — it should read as ambient fill, not a light.
  const bounce = new THREE.DirectionalLight(0xcbd6e0, 0.35);
  bounce.position.set(15, -40, 20);
  scene.add(bounce);
  scene.add(bounce.target);

  return { hemi, sun, fill, rim };
}
